import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Files, FileText, Image, FileSpreadsheet, File, Download, Eye, Search,
  Sparkles, Loader2, X, ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useTranslation } from '../i18n';

const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: 'text-red-500' },
  'image/png': { icon: Image, color: 'text-blue-500' },
  'image/jpeg': { icon: Image, color: 'text-blue-500' },
  'text/plain': { icon: FileSpreadsheet, color: 'text-green-500' },
  'application/msword': { icon: FileText, color: 'text-blue-600' },
};

function getFileIcon(mime) { return FILE_ICONS[mime] || { icon: File, color: 'text-muted-foreground' }; }

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const STATUS_COLORS = {
  uploaded: 'bg-amber-500/10 text-amber-600 border-amber-200',
  processed: 'bg-green-500/10 text-green-600 border-green-200',
  sent: 'bg-blue-500/10 text-blue-600 border-blue-200',
  signed: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
};

export default function MyDocumentsPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['my-documents'],
    queryFn: () => api.get('/documents/my-documents'),
  });

  const filtered = documents.filter(doc => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return doc.original_filename?.toLowerCase().includes(q) ||
           doc.deal_title?.toLowerCase().includes(q) ||
           doc.ai_extracted_data?.document_type?.toLowerCase().includes(q);
  });

  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewLoading(true);
    try {
      const url = await api.getDocumentPresignedUrl(doc._id);
      setPreviewUrl(url);
    } catch {
      setPreviewUrl(null);
    }
    setPreviewLoading(false);
  };

  const handleDownload = async (doc) => {
    try {
      const url = await api.getDocumentPresignedUrl(doc._id);
      window.open(url, '_blank');
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)]">
      <div className={`flex-1 flex flex-col min-w-0 transition-all ${previewDoc ? 'mr-[400px]' : ''}`}>
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h1 className="text-2xl font-bold tracking-tight">My Documents</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Documents shared with you across your deals and partnerships.
          </p>

          <div className="relative mt-4 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-card"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Files className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="text-lg font-semibold mb-1">
                {documents.length === 0 ? 'No documents yet' : 'No matches'}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm">
                {documents.length === 0
                  ? 'Documents shared with you will appear here.'
                  : 'Try a different search term.'}
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden bg-card">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Document</th>
                    <th className="text-left p-3 w-[100px] text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="text-left p-3 w-[140px] hidden md:table-cell text-xs font-semibold uppercase tracking-wider text-muted-foreground">Deal</th>
                    <th className="text-left p-3 w-[90px] text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                    <th className="text-left p-3 w-[80px] hidden md:table-cell text-xs font-semibold uppercase tracking-wider text-muted-foreground">Size</th>
                    <th className="w-[90px] p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(doc => {
                    const { icon: FileIcon, color } = getFileIcon(doc.mime_type);
                    const statusColor = STATUS_COLORS[doc.status] || STATUS_COLORS.uploaded;
                    const statusLabel = doc.status === 'processed' ? 'Analyzed' : (doc.status || 'uploaded').charAt(0).toUpperCase() + (doc.status || 'uploaded').slice(1);

                    return (
                      <tr
                        key={doc._id}
                        onClick={() => handlePreview(doc)}
                        className={`border-b last:border-0 cursor-pointer transition-colors hover:bg-muted/30 ${previewDoc?._id === doc._id ? 'bg-muted/40' : ''}`}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <FileIcon className={`h-5 w-5 shrink-0 ${color}`} />
                            <div className="min-w-0">
                              <span className="font-medium text-sm truncate block">{doc.original_filename}</span>
                              {doc.ai_extracted_data?.document_type && (
                                <span className="text-[10px] text-muted-foreground capitalize">{doc.ai_extracted_data.document_type}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className={`text-xs ${statusColor}`}>{statusLabel}</Badge>
                        </td>
                        <td className="p-3 hidden md:table-cell">
                          {doc.deal_title ? (
                            <span className="text-xs text-muted-foreground truncate block max-w-[120px]">{doc.deal_title}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">—</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-xs text-muted-foreground">{timeAgo(doc.created_at)}</span>
                        </td>
                        <td className="p-3 hidden md:table-cell">
                          <span className="text-xs text-muted-foreground">{formatBytes(doc.size)}</span>
                        </td>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(doc)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Preview Drawer */}
      {previewDoc && (
        <div className="fixed right-0 top-[64px] bottom-0 w-[400px] bg-background border-l shadow-xl z-30 flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h3 className="font-semibold text-sm truncate">{previewDoc.original_filename}</h3>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setPreviewDoc(null); setPreviewUrl(null); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Preview */}
            <div className="p-5 border-b">
              {previewLoading ? (
                <div className="h-48 rounded-lg bg-muted animate-pulse flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : previewUrl && previewDoc.mime_type?.startsWith('image/') ? (
                <img src={previewUrl} alt="" className="w-full rounded-lg border max-h-64 object-contain bg-muted/30" />
              ) : previewUrl && previewDoc.mime_type === 'application/pdf' ? (
                <iframe src={previewUrl} title="Preview" className="w-full h-64 rounded-lg border" />
              ) : (
                <div className="h-32 rounded-lg bg-muted/30 border-2 border-dashed flex flex-col items-center justify-center">
                  <File className="h-10 w-10 text-muted-foreground/50 mb-2" />
                  <p className="text-xs text-muted-foreground">Preview not available</p>
                </div>
              )}
            </div>

            {/* AI Analysis */}
            {previewDoc.ai_extracted_data && (
              <div className="p-5 border-b">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  <h4 className="text-sm font-semibold">AI Analysis</h4>
                </div>
                {previewDoc.ai_extracted_data.document_type && (
                  <p className="text-sm mb-2">
                    <span className="text-muted-foreground">Type: </span>
                    <span className="capitalize font-medium">{previewDoc.ai_extracted_data.document_type}</span>
                  </p>
                )}
                {previewDoc.ai_extracted_data.summary && (
                  <p className="text-sm text-foreground bg-muted/30 rounded-lg p-3 border">{previewDoc.ai_extracted_data.summary}</p>
                )}
              </div>
            )}

            {/* Details */}
            <div className="p-5">
              <h4 className="text-sm font-semibold mb-3">Details</h4>
              <div className="space-y-2 text-xs">
                {previewDoc.deal_title && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Deal</span>
                    <span className="font-medium">{previewDoc.deal_title}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Uploaded by</span>
                  <span className="font-medium">{previewDoc.uploaded_by_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">{previewDoc.created_at ? new Date(previewDoc.created_at).toLocaleDateString() : '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Size</span>
                  <span className="font-medium">{formatBytes(previewDoc.size)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t">
            <Button className="w-full gap-2" onClick={() => handleDownload(previewDoc)}>
              <Download className="h-4 w-4" /> Download
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
